import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import BaseSelect from "../../Base/BaseSelect";
import showToast from "../../../utils/toast";
import {
  ServiceEmailRequestSubmitData,
  ServiceEmailSenderOption,
  ServiceEmailStatus,
} from "../../../models/ServiceEmailRequestModel";
import useServiceEmailsService from "../../../services/useServiceEmailsService";
import useCompaniesService from "../../../services/useCompaniesService";
import "./styles/ServiceEmailBulkModal.css";

const MAX_EMAILS = 50;

type SolicitudRow = {
  key: string;
  passenger_count: string;
  request_date: string;
  request_time: string;
  origin_text: string;
  destination_text: string;
};

type EmailDraft = {
  key: string;
  company_id: string;
  subject: string;
  received_at: string;
  sender_ref: string;
  status: ServiceEmailStatus;
  solicitudes: SolicitudRow[];
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: () => void;
};

const createSolicitudRow = (): SolicitudRow => ({
  key: `${Date.now()}-${Math.random()}`,
  passenger_count: "1",
  request_date: "",
  request_time: "",
  origin_text: "",
  destination_text: "",
});

const copyTripFields = (
  source: SolicitudRow
): Pick<SolicitudRow, "request_date" | "request_time" | "origin_text" | "destination_text"> => ({
  request_date: source.request_date,
  request_time: source.request_time,
  origin_text: source.origin_text,
  destination_text: source.destination_text,
});

const createSolicitudRowFrom = (source?: SolicitudRow): SolicitudRow => ({
  ...createSolicitudRow(),
  ...(source ? copyTripFields(source) : {}),
});

const createEmailDraft = (source?: EmailDraft): EmailDraft => ({
  key: `${Date.now()}-${Math.random()}`,
  company_id: source?.company_id ?? "",
  subject: "",
  received_at: source?.received_at ?? "",
  sender_ref: source?.sender_ref ?? "",
  status: source?.status ?? "PENDIENTE",
  solicitudes: [createSolicitudRowFrom(source?.solicitudes?.[0])],
});

const buildSubmitData = (draft: EmailDraft): ServiceEmailRequestSubmitData => {
  const items = draft.solicitudes.map((row) => ({
    passenger_count: Math.max(1, Number(row.passenger_count) || 1),
    passenger_names: "",
    request_date: row.request_date || null,
    request_time: row.request_time || null,
    origin_text: row.origin_text.trim(),
    destination_text: row.destination_text.trim(),
  }));
  const first = items[0] ?? { passenger_count: 1 };

  return {
    email: {
      subject: draft.subject.trim(),
      received_at: draft.received_at || null,
      sender_ref: draft.sender_ref,
      has_multiple_requests: items.length > 1,
      request_date: first.request_date ?? null,
      request_time: first.request_time ?? null,
      passenger_count: first.passenger_count,
      origin_text: first.origin_text ?? "",
      destination_text: first.destination_text ?? "",
      status: draft.status,
    },
    solicitudes: items,
  };
};

const isDraftValid = (draft: EmailDraft) =>
  Boolean(draft.company_id) &&
  Boolean(draft.sender_ref) &&
  Boolean(draft.subject.trim()) &&
  draft.solicitudes.every((row) => Number(row.passenger_count) >= 1);

const ServiceEmailBulkModal: React.FC<Props> = ({ isOpen, onClose, onCreated }) => {
  const { store: storeServiceEmail, senderOptions: loadSenderOptions } = useServiceEmailsService();
  const { list: listCompanies } = useCompaniesService();

  const [quantity, setQuantity] = useState(1);
  const [emails, setEmails] = useState<EmailDraft[]>([createEmailDraft()]);
  const [companies, setCompanies] = useState<Array<{ value: string; label: string }>>([]);
  const [senderOptionsByCompany, setSenderOptionsByCompany] = useState<
    Record<string, ServiceEmailSenderOption[]>
  >({});
  const [loadingSendersByCompany, setLoadingSendersByCompany] = useState<Record<string, boolean>>(
    {}
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });
  const [globalError, setGlobalError] = useState<string | null>(null);

  const companiesRequestRef = useRef<AbortController | null>(null);
  const senderRequestsRef = useRef<Record<string, AbortController>>({});
  const senderOptionsCacheRef = useRef<Record<string, ServiceEmailSenderOption[]>>({});
  const loadingSendersRef = useRef<Record<string, boolean>>({});

  const companySelectOptions = useMemo(
    () => companies.map((company) => ({ value: company.value, label: company.label })),
    [companies]
  );

  const readyCount = useMemo(
    () => emails.filter((draft) => isDraftValid(draft)).length,
    [emails]
  );

  const totalSolicitudes = useMemo(
    () => emails.reduce((sum, email) => sum + email.solicitudes.length, 0),
    [emails]
  );

  useEffect(() => {
    if (!isOpen) return;
    setQuantity(1);
    setEmails([createEmailDraft()]);
    setSubmitted(false);
    setSaving(false);
    setSaveProgress({ current: 0, total: 0 });
    setGlobalError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    companiesRequestRef.current?.abort();
    const ctrl = new AbortController();
    companiesRequestRef.current = ctrl;

    listCompanies(
      {},
      {
        onSuccess: (resp: any) => {
          const items = Array.isArray(resp?.data?.companies) ? resp.data.companies : [];
          setCompanies(
            items.map((company: any) => ({
              value: String(company.id),
              label: company.name ?? company.company_name ?? "Sin nombre",
            }))
          );
        },
        onError: () => {
          if (ctrl.signal.aborted) return;
          showToast("Error cargando empresas", "error");
        },
      },
      ctrl.signal
    );

    return () => {
      ctrl.abort();
      Object.values(senderRequestsRef.current).forEach((request) => request.abort());
      senderRequestsRef.current = {};
    };
  }, [isOpen, listCompanies]);

  useEffect(() => {
    if (!isOpen) return;
    const n = Math.min(Math.max(1, quantity), MAX_EMAILS);
    setEmails((prev) => {
      if (prev.length === n) return prev;
      if (prev.length < n) {
        const last = prev[prev.length - 1];
        return [
          ...prev,
          ...Array.from({ length: n - prev.length }, () => createEmailDraft(last)),
        ];
      }
      return prev.slice(0, n);
    });
  }, [quantity, isOpen]);

  const ensureSenderOptions = useCallback((companyId: string) => {
    if (!companyId) return;
    if (senderOptionsCacheRef.current[companyId]) return;
    if (loadingSendersRef.current[companyId]) return;

    senderRequestsRef.current[companyId]?.abort();
    const ctrl = new AbortController();
    senderRequestsRef.current[companyId] = ctrl;
    loadingSendersRef.current[companyId] = true;
    setLoadingSendersByCompany((prev) => ({ ...prev, [companyId]: true }));

    loadSenderOptions(
      { company_id: companyId },
      {
        onSuccess: (resp: any) => {
          const data = resp?.data ?? {};
          const options = Array.isArray(data.sender_options) ? data.sender_options : [];
          senderOptionsCacheRef.current[companyId] = options;
          loadingSendersRef.current[companyId] = false;
          setSenderOptionsByCompany((prev) => ({ ...prev, [companyId]: options }));
          setLoadingSendersByCompany((prev) => ({ ...prev, [companyId]: false }));
        },
        onError: () => {
          if (ctrl.signal.aborted) return;
          loadingSendersRef.current[companyId] = false;
          setLoadingSendersByCompany((prev) => ({ ...prev, [companyId]: false }));
          showToast("Error cargando emisores de correo", "error");
        },
      },
      ctrl.signal
    );
  }, [loadSenderOptions]);

  const updateEmail = useCallback((key: string, patch: Partial<EmailDraft>) => {
    setEmails((prev) =>
      prev.map((email) => (email.key === key ? { ...email, ...patch } : email))
    );
    setGlobalError(null);
  }, []);

  const handleCompanyChange = useCallback(
    (emailKey: string, companyId: string) => {
      updateEmail(emailKey, { company_id: companyId, sender_ref: "" });
      ensureSenderOptions(companyId);
    },
    [ensureSenderOptions, updateEmail]
  );

  const updateSolicitud = useCallback(
    (emailKey: string, solicitudKey: string, patch: Partial<SolicitudRow>) => {
      setEmails((prev) =>
        prev.map((email) =>
          email.key !== emailKey
            ? email
            : {
                ...email,
                solicitudes: email.solicitudes.map((row) =>
                  row.key === solicitudKey ? { ...row, ...patch } : row
                ),
              }
        )
      );
      setGlobalError(null);
    },
    []
  );

  const addSolicitud = useCallback((emailKey: string) => {
    setEmails((prev) =>
      prev.map((email) => {
        if (email.key !== emailKey) return email;
        const last = email.solicitudes[email.solicitudes.length - 1];
        return {
          ...email,
          solicitudes: [...email.solicitudes, createSolicitudRowFrom(last)],
        };
      })
    );
  }, []);

  const removeSolicitud = useCallback((emailKey: string, solicitudKey: string) => {
    setEmails((prev) =>
      prev.map((email) => {
        if (email.key !== emailKey || email.solicitudes.length === 1) return email;
        return {
          ...email,
          solicitudes: email.solicitudes.filter((row) => row.key !== solicitudKey),
        };
      })
    );
  }, []);

  const removeEmail = useCallback((emailKey: string) => {
    setEmails((prev) => {
      if (prev.length === 1) return prev;
      const next = prev.filter((email) => email.key !== emailKey);
      setQuantity(next.length);
      return next;
    });
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    setGlobalError(null);

    const invalidIndex = emails.findIndex((draft) => !isDraftValid(draft));
    if (invalidIndex >= 0) {
      setGlobalError(
        `Revisa el correo #${invalidIndex + 1}: empresa, emisor, asunto y al menos 1 pax por solicitud.`
      );
      return;
    }

    setSaving(true);
    setSaveProgress({ current: 0, total: emails.length });

    let created = 0;

    for (let i = 0; i < emails.length; i += 1) {
      const draft = emails[i];
      setSaveProgress({ current: i + 1, total: emails.length });

      const { email, solicitudes } = buildSubmitData(draft);
      const resp: any = await storeServiceEmail({ email, solicitudes });
      const failed =
        !resp ||
        resp?.success === false ||
        (typeof resp?.http_status === "number" && resp.http_status >= 400);

      if (failed) {
        setGlobalError(resp?.message || `No se pudo guardar el correo #${i + 1}`);
        setSaving(false);
        return;
      }

      created += 1;
    }

    setSaving(false);

    showToast(`${created} correo(s) y sus solicitudes creados correctamente`, "success");

    onCreated?.();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="email-bulk-overlay" onClick={onClose}>
      <div
        className="email-bulk-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="email-bulk-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="email-bulk-header">
          <div>
            <h3 id="email-bulk-title" className="email-bulk-title">
              Carga masiva de correos
            </h3>
            <p className="email-bulk-subtitle">
              Un solo formulario tipo planilla: define la cantidad de correos y completa cada fila.
              Cada correo puede tener 1 a N solicitudes.
            </p>
            <p className="email-bulk-progress" aria-live="polite">
              {readyCount}/{emails.length} correos listos · {totalSolicitudes} solicitud
              {totalSolicitudes === 1 ? "" : "es"}
            </p>
          </div>
          <button
            type="button"
            className="email-bulk-close"
            onClick={onClose}
            aria-label="Cerrar"
            disabled={saving}
          >
            <X size={18} />
          </button>
        </header>

        <form className="email-bulk-form" onSubmit={handleSubmit}>
          <div className="email-bulk-body">
            {globalError && (
              <p className="email-bulk-global-error" role="alert">
                {globalError}
              </p>
            )}

            <div className="email-bulk-setup">
              <div className="email-bulk-setup__field">
                <label htmlFor="email-bulk-qty">Cantidad de correos</label>
                <div className="email-bulk-qty-control" role="group" aria-label="Cantidad de correos">
                  <button
                    type="button"
                    className="email-bulk-qty-btn"
                    onClick={() => setQuantity((prev) => Math.max(1, prev - 1))}
                    disabled={saving || quantity <= 1}
                    aria-label="Disminuir cantidad"
                  >
                    -
                  </button>
                  <input
                    id="email-bulk-qty"
                    type="number"
                    min={1}
                    max={MAX_EMAILS}
                    value={quantity}
                    onChange={(event) => {
                      const value = Number(event.target.value);
                      if (Number.isNaN(value)) return;
                      setQuantity(Math.min(MAX_EMAILS, Math.max(1, value)));
                    }}
                    disabled={saving}
                  />
                  <button
                    type="button"
                    className="email-bulk-qty-btn"
                    onClick={() => setQuantity((prev) => Math.min(MAX_EMAILS, prev + 1))}
                    disabled={saving || quantity >= MAX_EMAILS}
                    aria-label="Aumentar cantidad"
                  >
                    +
                  </button>
                </div>
              </div>
              <p className="email-bulk-setup__hint">
                Máximo {MAX_EMAILS}. Al aumentar se copia empresa/emisor del último correo.
              </p>
            </div>

            <div className="email-bulk-table-wrap">
              <table className="email-bulk-table">
                <thead>
                  <tr>
                    <th className="email-bulk-col-num">#</th>
                    <th className="email-bulk-col-company">Empresa</th>
                    <th className="email-bulk-col-sender">Emisor</th>
                    <th className="email-bulk-col-subject">Asunto</th>
                    <th className="email-bulk-col-datetime">Recibido</th>
                    <th className="email-bulk-col-status">Estado</th>
                    <th className="email-bulk-col-sol">#Sol</th>
                    <th className="email-bulk-col-pax">Pax</th>
                    <th className="email-bulk-col-date">Día</th>
                    <th className="email-bulk-col-time">Hora</th>
                    <th>Origen</th>
                    <th>Destino</th>
                    <th className="email-bulk-col-actions">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {emails.map((email, emailIndex) => {
                    const senderOptions = senderOptionsByCompany[email.company_id] ?? [];
                    const loadingSenders = Boolean(loadingSendersByCompany[email.company_id]);
                    const senderSelectOptions = senderOptions.map((option) => ({
                      value: option.value,
                      label: option.label,
                    }));
                    const senderDisabled = !email.company_id || loadingSenders;
                    const rowSpan = Math.max(email.solicitudes.length, 1);
                    const emailInvalid = submitted && !isDraftValid(email);
                    const tone = emailIndex % 8;

                    return email.solicitudes.map((solicitud, solIndex) => {
                      const isFirst = solIndex === 0;
                      return (
                        <tr
                          key={`${email.key}-${solicitud.key}`}
                          className={`email-bulk-row email-bulk-row--tone-${tone}${
                            emailInvalid ? " is-invalid" : ""
                          }${isFirst ? "" : " email-bulk-row--sub"}`}
                        >
                          {isFirst && (
                            <>
                              <td className="email-bulk-col-num" rowSpan={rowSpan}>
                                {emailIndex + 1}
                              </td>
                              <td className="email-bulk-col-company" rowSpan={rowSpan}>
                                <BaseSelect
                                  label=""
                                  name={`company_${email.key}`}
                                  value={email.company_id}
                                  placeholder="Empresa"
                                  searchable
                                  options={companySelectOptions}
                                  onChange={(event) =>
                                    handleCompanyChange(email.key, event.target.value)
                                  }
                                />
                              </td>
                              <td className="email-bulk-col-sender" rowSpan={rowSpan}>
                                <BaseSelect
                                  label=""
                                  name={`sender_${email.key}`}
                                  value={email.sender_ref}
                                  placeholder={
                                    email.company_id ? "Emisor" : "Empresa primero"
                                  }
                                  searchable
                                  preserveCase
                                  disabled={senderDisabled}
                                  options={senderSelectOptions}
                                  onChange={(event) =>
                                    updateEmail(email.key, { sender_ref: event.target.value })
                                  }
                                />
                              </td>
                              <td className="email-bulk-col-subject" rowSpan={rowSpan}>
                                <input
                                  type="text"
                                  value={email.subject}
                                  onChange={(event) =>
                                    updateEmail(email.key, { subject: event.target.value })
                                  }
                                  placeholder="Asunto"
                                  className={
                                    submitted && !email.subject.trim() ? "has-error" : undefined
                                  }
                                />
                              </td>
                              <td className="email-bulk-col-datetime" rowSpan={rowSpan}>
                                <input
                                  type="datetime-local"
                                  value={email.received_at}
                                  onChange={(event) =>
                                    updateEmail(email.key, { received_at: event.target.value })
                                  }
                                />
                              </td>
                              <td className="email-bulk-col-status" rowSpan={rowSpan}>
                                <select
                                  value={email.status}
                                  onChange={(event) =>
                                    updateEmail(email.key, {
                                      status: event.target.value as ServiceEmailStatus,
                                    })
                                  }
                                >
                                  <option value="PENDIENTE">Pendiente</option>
                                  <option value="AGREGADO">Agregado</option>
                                </select>
                              </td>
                            </>
                          )}

                          <td className="email-bulk-col-sol">{solIndex + 1}</td>
                          <td className="email-bulk-col-pax">
                            <input
                              type="number"
                              min={1}
                              value={solicitud.passenger_count}
                              onChange={(event) =>
                                updateSolicitud(email.key, solicitud.key, {
                                  passenger_count: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td className="email-bulk-col-date">
                            <input
                              type="date"
                              value={solicitud.request_date}
                              onChange={(event) =>
                                updateSolicitud(email.key, solicitud.key, {
                                  request_date: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td className="email-bulk-col-time">
                            <input
                              type="time"
                              value={solicitud.request_time}
                              onChange={(event) =>
                                updateSolicitud(email.key, solicitud.key, {
                                  request_time: event.target.value,
                                })
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={solicitud.origin_text}
                              onChange={(event) =>
                                updateSolicitud(email.key, solicitud.key, {
                                  origin_text: event.target.value,
                                })
                              }
                              placeholder="Origen"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={solicitud.destination_text}
                              onChange={(event) =>
                                updateSolicitud(email.key, solicitud.key, {
                                  destination_text: event.target.value,
                                })
                              }
                              placeholder="Destino"
                            />
                          </td>
                          <td className="email-bulk-col-actions">
                            <div className="email-bulk-actions">
                              {isFirst && (
                                <button
                                  type="button"
                                  className="email-bulk-action email-bulk-action--add"
                                  onClick={() => addSolicitud(email.key)}
                                  disabled={saving}
                                >
                                  + Sol
                                </button>
                              )}
                              {email.solicitudes.length > 1 && (
                                <button
                                  type="button"
                                  className="email-bulk-action email-bulk-action--remove"
                                  onClick={() => removeSolicitud(email.key, solicitud.key)}
                                  disabled={saving}
                                >
                                  Quitar sol
                                </button>
                              )}
                              {isFirst && emails.length > 1 && (
                                <button
                                  type="button"
                                  className="email-bulk-action email-bulk-action--remove"
                                  onClick={() => removeEmail(email.key)}
                                  disabled={saving}
                                >
                                  Quitar correo
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <footer className="email-bulk-footer">
            <button
              type="button"
              className="email-bulk-btn email-bulk-btn--ghost"
              onClick={onClose}
              disabled={saving}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="email-bulk-btn email-bulk-btn--primary"
              disabled={saving || emails.length === 0}
            >
              {saving
                ? `Guardando ${saveProgress.current}/${saveProgress.total}...`
                : `Crear ${emails.length} correo${emails.length === 1 ? "" : "s"}`}
            </button>
          </footer>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ServiceEmailBulkModal;
